/**
 * Хост бэкенда (Bitrix API + статика /upload/) витрины.
 *
 * Модуль-лист: ничего не импортирует, его безопасно тянуть из proxy.js,
 * серверного кода и клиентских компонентов.
 *
 * Переменные окружения (все необязательные; без них поведение = прод api.ur-met.ru):
 *   NEXT_PUBLIC_API_URL  origin бэкенда. Дефолт https://api.ur-met.ru.
 *                        NEXT_PUBLIC_* инлайнится в клиентский бандл при build.
 *   API_HOSTS            все хосты бэкенда, чьи картинки витрина принимает
 *                        (next/image remotePatterns, нормализация URL картинок),
 *                        через запятую. Хост из NEXT_PUBLIC_API_URL добавляется
 *                        всегда. Дефолт api.ur-met.ru — старый хост остаётся
 *                        допустимым и после переключения, т.к. в Elasticsearch и
 *                        в контенте Bitrix картинки лежат с абсолютным api.ur-met.ru.
 *                        Пробрасывается в клиент через next.config.js `env` (build-time).
 *   API_URL_CONTENT      база REST-сервисов. Дефолт <NEXT_PUBLIC_API_URL>/api/.
 *   URL_CONTENT          база /api/next/. Дефолт <NEXT_PUBLIC_API_URL>/api/next/.
 *
 * Переключение на новый хост: NEXT_PUBLIC_API_URL=https://api.kontinental.pro,
 * API_HOSTS=api.kontinental.pro,api.ur-met.ru и либо убрать API_URL_CONTENT/URL_CONTENT,
 * либо выставить их на тот же хост.
 *
 * Логика чтения NEXT_PUBLIC_API_URL/API_HOSTS продублирована в next.config.js
 * (CommonJS, выполняется до сборки TS) — tests/api-host.test.js сверяет обе реализации.
 */

export const DEFAULT_API_ORIGIN = 'https://api.ur-met.ru';
/** Хосты бэкенда, которые принимаются, если API_HOSTS не задан. */
export const DEFAULT_API_HOSTS = ['api.ur-met.ru'];
/**
 * Старый хост сайта: в данных бэкенда встречаются http://ur-met.ru/upload/...
 * (без api.) — такие URL нормализуются на бэкенд так же, как http://api.ur-met.ru/upload/...
 */
const LEGACY_UPLOAD_HOSTS = ['ur-met.ru'];

export interface ApiHostConfig {
    /** Origin бэкенда без завершающего слэша: https://api.ur-met.ru */
    origin: string;
    /** Хост бэкенда: api.ur-met.ru */
    host: string;
    /** Все допустимые хосты бэкенда: текущий + API_HOSTS (или дефолт) */
    hosts: string[];
    /** База REST-сервисов со слэшем: https://api.ur-met.ru/api/ */
    contentBase: string;
    /** База /api/next/ со слэшем: https://api.ur-met.ru/api/next/ */
    nextContentBase: string;
}

type Env = Record<string, string | undefined>;

function normalizeHost(value?: string | null): string {
    if (!value) return '';
    return String(value).trim().toLowerCase()
        .replace(/^[a-z]+:\/\//, '')
        .replace(/\/.*$/, '')
        .replace(/:\d+$/, '')
        .replace(/\.$/, '');
}

function withTrailingSlash(value: string): string {
    return value.endsWith('/') ? value : `${value}/`;
}

export function readApiHostConfig(env: Env): ApiHostConfig {
    const raw = (env.NEXT_PUBLIC_API_URL || '').trim().replace(/\/+$/, '');
    let origin = DEFAULT_API_ORIGIN;
    let host = normalizeHost(DEFAULT_API_ORIGIN);
    if (raw) {
        try {
            const url = new URL(raw);
            origin = url.origin;
            host = url.hostname.toLowerCase();
        } catch {
            // Невалидное значение — остаёмся на дефолте, а не ломаем сборку.
        }
    }
    const listed = (env.API_HOSTS || '').split(',').map(normalizeHost).filter(Boolean);
    const hosts = Array.from(new Set([host, ...(listed.length ? listed : DEFAULT_API_HOSTS)]));
    const contentBase = (env.API_URL_CONTENT || '').trim() || `${origin}/api/`;
    const nextContentBase = (env.URL_CONTENT || '').trim() || `${origin}/api/next/`;
    return {
        origin,
        host,
        hosts,
        contentBase: withTrailingSlash(contentBase),
        nextContentBase: withTrailingSlash(nextContentBase),
    };
}

/**
 * Конфиг из окружения. Ссылки на process.env.X — буквальные: так Next.js
 * инлайнит NEXT_PUBLIC_API_URL и API_HOSTS (из next.config.js `env`) в клиент.
 * Без кеша: чтение дешёвое, а тесты могут менять process.env.
 */
export function getApiHostConfig(): ApiHostConfig {
    return readApiHostConfig({
        NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
        API_HOSTS: process.env.API_HOSTS,
        API_URL_CONTENT: process.env.API_URL_CONTENT,
        URL_CONTENT: process.env.URL_CONTENT,
    });
}

/** https://api.ur-met.ru — без завершающего слэша. */
export function getApiOrigin(): string {
    return getApiHostConfig().origin;
}

/** api.ur-met.ru */
export function getApiHost(): string {
    return getApiHostConfig().host;
}

/** Все допустимые хосты бэкенда (новый + старые), для remotePatterns и нормализации картинок. */
export function getApiOrigins(): string[] {
    return getApiHostConfig().hosts;
}

/** apiUrl('/api/services/faq/index.php') -> https://api.ur-met.ru/api/services/faq/index.php */
export function apiUrl(path: string = '/'): string {
    return `${getApiOrigin()}/${String(path).replace(/^\/+/, '')}`;
}

/** База REST-сервисов: API_URL_CONTENT || <origin>/api/ (со слэшем). */
export function getApiContentBase(): string {
    return getApiHostConfig().contentBase;
}

/** База /api/next/: URL_CONTENT || <origin>/api/next/ (со слэшем). */
export function getNextContentBase(): string {
    return getApiHostConfig().nextContentBase;
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * http://(api.)ur-met.ru/upload/... и http://<любой хост из API_HOSTS>/upload/...
 * -> <origin бэкенда>/upload/... Остальные URL возвращаются как есть.
 */
export function normalizeApiUploadUrl(value: string): string {
    const hosts = Array.from(new Set([...getApiOrigins(), ...LEGACY_UPLOAD_HOSTS]));
    const pattern = new RegExp(`^http://(?:${hosts.map(escapeRegExp).join('|')})/upload/`, 'i');
    return value.replace(pattern, `${getApiOrigin()}/upload/`);
}
